import {
	Heading,
	Container,
	Page,
	Section,
	Button,
	Grid,
	GridFive,
	GridSeven,
	HorizontalRule,
	Link,
	LinkButton,
	Separator,
	Hero,
	Dialog,
} from '@troon/ui';
import { Title } from '@solidjs/meta';
import { createAsync, revalidate } from '@solidjs/router';
import { cachedGet } from '@troon/api-client';
import { Suspense, Switch, Match, Show } from 'solid-js';
import { getConfigValue } from '../../../modules/config';
import { AuthFlow } from '../../../partials/auth/auth';
import { useUser } from '../../../providers/user';
import { title, VisaBenefits } from './visa(confirmation)/verify/_content';
import type { RouteDefinition } from '@solidjs/router';

export default function VisaInfinitePage() {
	const user = useUser();
	const status = createAsync(() => getVisaStatus({}), { deferStream: true });
	return (
		<>
			<Title>Visa Golf Benefits | Golf with Access</Title>
			<Hero src={`${getConfigValue('IMAGE_HOST')}/hero/member-programs.jpg`}>
				<Heading as="h1">Visa Golf Benefits</Heading>
				<p>by Access</p>
			</Hero>

			<Container>
				<Page>
					<Grid>
						<GridSeven>
							<Section>
								<Heading as="h2">Enjoy Premium Benefits</Heading>
								<p>
									Qualifying Visa cards entitle you to automatic upgraded status with Access, saving on access tee times
									and more!
								</p>
								<p>
									Visa Infinite® US and Visa Infinite Privilege® CA cards also gives access to over 20 high-end, private
									Troon Privé golf clubs. Play each club two times per year for $99 per player per round when you use
									your Visa Infinite card to book, and bring up to three guests at the same rate.
								</p>
							</Section>
						</GridSeven>
						<GridFive>
							<Section appearance="contained" class="bg-neutral-100">
								<Suspense>
									<Switch>
										<Match when={status()?.data}>
											{(program) => (
												<>
													<Heading as="h2" size="h4">
														Your Benefits
													</Heading>

													<p>Your {title[program().programType]} card is verified and benefits have been activated!</p>

													<VisaBenefits program={program().programType} privateEligible={program().privateEligible} />

													<Show when={program().canUpgrade}>
														<HorizontalRule />
														<p>Have another qualifying Visa card? Verify it now to redeem your benefits.</p>
														<Button as={Link} href="/membership/visa/verify/visa-infinite" appearance="tertiary">
															Verify your card
														</Button>
													</Show>
												</>
											)}
										</Match>
										<Match when={!status()?.data?.privateEligible}>
											<Heading as="h2" size="h4">
												Redeem Your Visa Benefits
											</Heading>

											<Show when={!user()}>
												<p>
													Already an Access member?{' '}
													<Dialog key="auth">
														<Dialog.Trigger asChild={(p) => <LinkButton {...p()} class="-mx-2" />}>
															Log in now
														</Dialog.Trigger>
														<Dialog.Container desktopWidth="sm">
															<div class="absolute inset-e-2 inset-bs-2">
																<Dialog.CloseTrigger />
															</div>
															<AuthFlow onComplete={() => revalidate('/v0/visa/status')} />
														</Dialog.Container>
													</Dialog>{' '}
													to see your benefits.
												</p>
												<Separator>OR</Separator>
											</Show>

											<p>Select your qualifying Visa card to redeem your benefits now.</p>

											<Heading as="h3" size="h5">
												US Cardholders
											</Heading>

											<Button as={Link} href="/membership/visa/verify/visa-signature" appearance="tertiary">
												Visa Signature®
											</Button>
											<Button as={Link} href="/membership/visa/verify/visa-infinite" appearance="tertiary">
												Visa Infinite®
											</Button>

											<Heading as="h3" size="h5">
												Canadian Cardholders
											</Heading>
											<Button as={Link} href="/membership/visa/verify/visa-infinite-ca" appearance="tertiary">
												Visa Infinite®
											</Button>
											<Button as={Link} href="/membership/visa/verify/visa-infinite-privilege-ca" appearance="tertiary">
												Visa Infinite Privilege®
											</Button>
										</Match>
									</Switch>
								</Suspense>
							</Section>
						</GridFive>
					</Grid>
				</Page>
			</Container>
		</>
	);
}

export const route = { info: { nav: { hero: true } } } satisfies RouteDefinition;

const getVisaStatus = cachedGet('/v0/visa/status', {}, { redirect401: false });
